import axios, { type AxiosInstance } from 'axios';

// SAM-633 — public pay page for a booking payment link (`/pay/:token`).
//
// Contract: sam-lambda-functions `functions/booking/payment_link.py` (get_public_summary /
// _public_view, run_3ds_step, pay, to_api_response). The three `{token}` routes are `public` in
// API Gateway (infra/gateway-route-auth.yml: no authorizer, no API key) — the 43-char token in the
// path is the payer's only credential.
//
// Why a dedicated axios instance and not `instance`/`directApiInstance` from httpClient.ts: those
// queue every request until the admin's business data loads and inject the admin's
// `Authorization: Bearer` + `X-Api-Key`. A guest has neither, and when an admin is signed in on the
// same browser their session must never ride along to a public payment route. Same approach as
// publicApi.ts (its own axios.create, no interceptors), with one extra guard below that strips any
// credential header a global default could add.

export const PAYMENT_LINK_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

export const isWellFormedPaymentLinkToken = (token: string | undefined | null): token is string =>
  typeof token === 'string' && PAYMENT_LINK_TOKEN_RE.test(token);

export const createPaymentLinkClient = (baseURL: string | undefined): AxiosInstance => {
  const client = axios.create({
    baseURL,
    // No cookies: nothing on these routes is session-based.
    withCredentials: false,
    timeout: 90_000,
  });
  client.interceptors.request.use((config) => {
    const headers = config.headers as unknown as Record<string, unknown> & { delete?: (k: string) => void };
    for (const name of ['Authorization', 'authorization', 'X-Api-Key', 'x-api-key']) {
      if (typeof headers?.delete === 'function') headers.delete(name);
      else if (headers) delete headers[name];
    }
    return config;
  });
  return client;
};

const client = createPaymentLinkClient(import.meta.env.VITE_API_BASE_URL);

const path = (token: string, suffix = '') => `/bookings/payment-link/${encodeURIComponent(token)}${suffix}`;

// ─── Response shapes (payment_link._public_view) ─────────────────────────────

export interface LocalizedText {
  en?: string | null;
  es?: string | null;
}

export interface PaymentLinkLineItem {
  id?: string | null;
  label: LocalizedText;
  quantity?: number | null;
  unit_price?: number | string | null;
  subtotal?: number | string | null;
  currency?: string | null;
  type?: 'guest_type' | 'add_on' | string;
}

export interface PaymentLinkReceipt {
  confirmation_code?: string | null;
  paid_at?: string | null;
  total?: number | string | null;
  currency?: string | null;
  payment_auth_code?: string | null;
}

/**
 * The entry pass of a paid link's booking (booking_access.public_pass_view). Present only on a paid
 * summary inside the 24 h receipt window. `qr_value` is what the gate resolves (the access id) and is
 * only set when `state` is `ready`; `pass_url` is the tenant's public pass page (it outlives this link).
 */
export type PaymentLinkPassState = 'ready' | 'verification_required' | 'used' | 'cancelled' | 'unavailable';

export interface PaymentLinkPass {
  state: PaymentLinkPassState | string;
  qr_value?: string | null;
  pass_url?: string | null;
  /** Business-local, with offset (e.g. 2026-10-05T00:00:00-04:00). */
  valid_from?: string | null;
  valid_until?: string | null;
  timezone?: string | null;
  guests?: { total?: number | null; children?: number | null } | null;
  id_verified?: boolean | null;
  door_pin?: { status?: string | null; pin?: string | null } | null;
}

export type PaymentLinkStatus =
  | 'pending_payment'
  | 'processing'
  | 'paid'
  | 'expired'
  | 'revoked'
  | 'cancelled';

export interface PaymentLinkSummary {
  status: PaymentLinkStatus | string;
  payable: boolean;
  expires_at?: string | null;
  seconds_remaining?: number | null;
  business?: { name?: string | null; logo_url?: string | null };
  service?: { name?: LocalizedText; type?: string | null };
  reservation_start?: string | null;
  reservation_end?: string | null;
  guests?: { total?: number | null; first_name?: string | null };
  line_items?: PaymentLinkLineItem[];
  total?: number | string | null;
  currency?: string | null;
  requires_3ds?: boolean;
  /** Present only on a locked link ("Too many payment attempts ..."). */
  message?: string;
  receipt?: PaymentLinkReceipt;
  pass?: PaymentLinkPass | null;
}

export interface PaymentLinkEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  error_code?: string;
}

// ─── Request shapes ──────────────────────────────────────────────────────────

export interface PaymentLinkCard {
  number: string;
  expiration_month: string;
  expiration_year: string;
  security_code: string;
}

export interface PaymentLinkAuthData {
  cavv?: string;
  eci?: string;
  eci_raw?: string;
  pares_status?: string;
  xid?: string;
  indicator?: string;
  acs_transaction_id?: string;
  directory_server_transaction_id?: string;
  authentication_transaction_id?: string;
  strong_authentication?: Record<string, string>;
}

/** Body of POST /{token}/3ds. Amount, currency and merchant come from the link server-side. */
export interface PaymentLink3dsBody {
  step: 'setup' | 'enroll' | 'validate';
  card: PaymentLinkCard;
  cardholder_name: string;
  return_url?: string;
  reference_id?: string;
  merchant_reference?: string;
  authentication_transaction_id?: string;
}

/** Body of POST /{token}/pay. Never carries an amount — the server charges the stored total.
 * No `auth_data` when the summary says `requires_3ds: false` (SAM-1171 pass links, Cardnet). */
export interface PaymentLinkPayBody {
  card: PaymentLinkCard;
  cardholder_name: string;
  auth_data?: PaymentLinkAuthData;
}

// 3DS step responses: the same snake_case shapes as /bookings/payment/{setup,enroll,validate}
// (lambda_function.payment_setup/enroll/validate), which run_3ds_step forwards.
export interface PaymentLinkSetupData {
  reference_id: string;
  device_data_collection_url: string;
  access_token: string;
  merchant_reference?: string;
}

export interface PaymentLinkEnrollData {
  status: 'AUTHENTICATION_SUCCESSFUL' | 'PENDING_AUTHENTICATION' | string;
  auth_data?: PaymentLinkAuthData;
  step_up_url?: string;
  access_token?: string;
  authentication_transaction_id?: string;
}

export interface PaymentLinkValidateData {
  status: string;
  auth_data?: PaymentLinkAuthData;
}

// ─── Calls ───────────────────────────────────────────────────────────────────
// Each returns the unwrapped envelope `data`; non-2xx responses reject with the axios error, which
// paymentLinkModel.classifyPaymentLinkError turns into a page outcome. The 202 PAYMENT_PROCESSING
// answer is a 2xx: callers read `error_code` off the envelope, so pay() returns the whole envelope.

export async function getPaymentLinkSummary(token: string, http: AxiosInstance = client): Promise<PaymentLinkSummary> {
  // No custom request headers: the route's CORS preflight only allows Content-Type, Authorization,
  // X-Amz-Date, X-Api-Key and X-Amz-Security-Token. Freshness is the server's job
  // (`Cache-Control: no-store` on every response, payment_link.to_api_response).
  const res = await http.get<PaymentLinkEnvelope<PaymentLinkSummary>>(path(token));
  return res.data.data as PaymentLinkSummary;
}

export async function postPaymentLink3ds<T>(token: string, body: PaymentLink3dsBody, http: AxiosInstance = client): Promise<T> {
  const res = await http.post<PaymentLinkEnvelope<T>>(path(token, '/3ds'), body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return res.data.data as T;
}

export async function payPaymentLink(
  token: string,
  body: PaymentLinkPayBody,
  http: AxiosInstance = client,
): Promise<{ status: number; envelope: PaymentLinkEnvelope<PaymentLinkSummary | { status?: string }> }> {
  const res = await http.post<PaymentLinkEnvelope<PaymentLinkSummary | { status?: string }>>(path(token, '/pay'), body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return { status: res.status, envelope: res.data };
}
